import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useToast } from '../../hooks/useToast';
import { adminService } from '../../services/admin';
import { documentService } from '../../services/documents';
import {
  RegistrationPetition,
  RegistrationApplicationStatus,
  RegistrationStatus,
  RegistrationReviewStatus,
} from '../../types/admin';
import { normalizeRegistrationStatus } from '../../services/applicationStatus';
import {
  MemberPetition,
  MemberPetitionStatus,
  MemberPetitionReviewStatus,
  PetitionType,
} from '../../types/petition';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { formatAthensDateTime } from '../../utils/athensTime';
import {
  formatPetitionPayload,
  membershipChangeIsApprovable,
  membershipTypeLabel,
  parseMembershipChangePayload,
} from '../../utils/petitionPayload';
import { normalizePetitionType } from '../../utils/petition';
import { Container, Stack } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text, Label } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import { Tabs, TabPanel } from '../../design-system/patterns/Tabs';
import { DataTable, Column } from '../../components/common/DataTable';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const PetitionsContainer = styled(Container)`
  max-width: 100%;
  width: 100%;
`;

const FiltersSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  align-items: end;
`;

const FilterLabel = styled.label`
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  display: block;
  margin-bottom: ${spacing[2]};
`;

const SelectStyled = styled.select`
  padding: ${spacing[3]} ${spacing[4]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  width: 100%;
`;

const ActionsCell = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${spacing[2]};
`;

const StatusBadge = styled.span<{ $status: number }>`
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borders.radius.full};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  background: ${props => {
    if (props.$status === 2) return props.theme.colors.success + '30';
    if (props.$status === 3) return props.theme.colors.error + '30';
    if (props.$status === 4) return (props.theme.colors.info || props.theme.colors.primary) + '30';
    return props.theme.colors.warning + '30';
  }};
  color: ${props => {
    if (props.$status === 2) return props.theme.colors.success;
    if (props.$status === 3) return props.theme.colors.error;
    if (props.$status === 4) return props.theme.colors.info || props.theme.colors.primary;
    return props.theme.colors.warning;
  }};
`;

const PAGE_SIZE = 20;

function regStatusLabel(status: number | string, t: (k: string) => string): string {
  const n = typeof status === 'number' ? status : Number(status);
  if (status === 'Pending' || n === 1) return t('petitions.statusPending');
  if (status === 'Approved' || n === 2) return t('petitions.statusApproved');
  if (status === 'Rejected' || n === 3) return t('petitions.statusRejected');
  if (status === 'NeedsChanges' || n === 4) return t('petitions.statusNeedsChanges');
  return String(status);
}

function memberPetitionTypeLabel(type: PetitionType, t: (k: string) => string): string {
  switch (type) {
    case PetitionType.LostMemberCard:
      return t('petitions.types.lostMemberCard');
    case PetitionType.ChangeMembershipType:
      return t('petitions.types.changeMembershipType');
    default:
      return String(type);
  }
}

function memberPetitionStatusLabel(status: MemberPetitionStatus, t: (k: string) => string): string {
  switch (status) {
    case MemberPetitionStatus.Pending:
      return t('petitions.statusPending');
    case MemberPetitionStatus.Approved:
      return t('petitions.statusApproved');
    case MemberPetitionStatus.Rejected:
      return t('petitions.statusRejected');
    default:
      return String(status);
  }
}

const AdminRegistrationPetitions: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success: showSuccess, error: showError } = useToast();
  const [petitions, setPetitions] = useState<RegistrationPetition[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RegistrationApplicationStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<'registration' | 'member'>(() =>
    searchParams.get('tab') === 'member' ? 'member' : 'registration'
  );
  const [memberPetitions, setMemberPetitions] = useState<MemberPetition[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberPage, setMemberPage] = useState(1);
  const [memberTotal, setMemberTotal] = useState(0);
  const [memberStatusFilter, setMemberStatusFilter] = useState<MemberPetitionStatus | ''>('');
  const [memberTypeFilter, setMemberTypeFilter] = useState<PetitionType | ''>('');
  const [memberRejectModalOpen, setMemberRejectModalOpen] = useState(false);
  const [memberRejectingId, setMemberRejectingId] = useState<string | null>(null);
  const [memberRejectionReason, setMemberRejectionReason] = useState('');
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [memberDetailPetition, setMemberDetailPetition] = useState<MemberPetition | null>(null);
  const [memberApprovePetition, setMemberApprovePetition] = useState<MemberPetition | null>(null);

  useEffect(() => {
    const tab = searchParams.get('tab') === 'member' ? 'member' : 'registration';
    setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (id: string) => {
    const next = id === 'member' ? 'member' : 'registration';
    setActiveTab(next);
    if (next === 'member') {
      setSearchParams({ tab: 'member' });
    } else {
      setSearchParams({});
    }
  };

  const handleDownloadAttachment = async (docId: string, fileName?: string) => {
    try {
      const blob = await documentService.downloadDocument(docId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'attachment';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('petitions.loadError'));
    }
  };

  useEffect(() => {
    if (activeTab !== 'registration') return;
    const load = async () => {
      setLoading(true);
      try {
        const statusParam = statusFilter === '' ? undefined : (statusFilter as RegistrationApplicationStatus);
        const typeParam = typeFilter === '' ? undefined : typeFilter;
        const res = await adminService.getRegistrationPetitions(page, PAGE_SIZE, statusParam, typeParam);
        setPetitions(res.items);
        setTotal(res.total);
      } catch (err) {
        console.error(err);
        showError(t('petitions.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTab, page, statusFilter, typeFilter, t, showError]);

  useEffect(() => {
    if (activeTab !== 'member') return;
    const load = async () => {
      setMemberLoading(true);
      try {
        const typeParam = memberTypeFilter === '' ? undefined : memberTypeFilter;
        const statusParam = memberStatusFilter === '' ? undefined : memberStatusFilter;
        const res = await adminService.getMemberPetitions(memberPage, PAGE_SIZE, typeParam, statusParam);
        setMemberPetitions(res.items);
        setMemberTotal(res.total);
      } catch (err) {
        console.error(err);
        showError(t('petitions.loadError'));
      } finally {
        setMemberLoading(false);
      }
    };
    load();
  }, [activeTab, memberPage, memberTypeFilter, memberStatusFilter, t, showError]);

  const handleApprove = async (id: string) => {
    setActionLoading(true);
    try {
      await adminService.reviewPetition(id, { status: RegistrationReviewStatus.Approved });
      showSuccess(t('petitions.approvedSuccess'));
      if (statusFilter === '') {
        setPetitions(prev =>
          prev.map(p =>
            p.id === id
              ? { ...p, status: RegistrationStatus.Approved as typeof p.status }
              : p
          )
        );
      } else {
        setPetitions(prev => prev.filter(p => p.id !== id));
        setTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      showError(err.message || t('petitions.reviewFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (id: string) => {
    setRejectingId(id);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectingId) return;
    setActionLoading(true);
    try {
      await adminService.reviewPetition(rejectingId, {
        status: RegistrationReviewStatus.Rejected,
        rejectionReason: rejectionReason.trim() || undefined,
      });
      showSuccess(t('petitions.rejectedSuccess'));
      setRejectModalOpen(false);
      const rejectedId = rejectingId;
      setRejectingId(null);
      // When viewing "All", keep the row and mark Rejected; otherwise remove from filtered pending list.
      if (statusFilter === '') {
        setPetitions(prev =>
          prev.map(p =>
            p.id === rejectedId
              ? { ...p, status: RegistrationStatus.Rejected as typeof p.status }
              : p
          )
        );
      } else {
        setPetitions(prev => prev.filter(p => p.id !== rejectedId));
        setTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      showError(err.message || t('petitions.reviewFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleView = (p: RegistrationPetition) => {
    navigate(`/admin/petitions/${p.id}`);
  };

  const handleMemberApprove = async (petition: MemberPetition) => {
    if (!membershipChangeIsApprovable(petition)) {
      showError(
        t('petitions.cannotApproveMissingTarget', {
          defaultValue: 'Cannot approve: target membership category is missing.',
        })
      );
      return;
    }
    setMemberActionLoading(true);
    try {
      await adminService.reviewMemberPetition(petition.id, { status: MemberPetitionReviewStatus.Approved });
      showSuccess(t('petitions.approvedSuccess'));
      setMemberApprovePetition(null);
      setMemberDetailPetition(null);
      if (memberStatusFilter === '') {
        setMemberPetitions(prev =>
          prev.map(p => (p.id === petition.id ? { ...p, status: MemberPetitionStatus.Approved } : p))
        );
      } else {
        setMemberPetitions(prev => prev.filter(p => p.id !== petition.id));
        setMemberTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      showError(err.message || t('petitions.reviewFailed'));
    } finally {
      setMemberActionLoading(false);
    }
  };

  const openMemberRejectModal = (id: string) => {
    setMemberRejectingId(id);
    setMemberRejectionReason('');
    setMemberRejectModalOpen(true);
  };

  const handleMemberRejectSubmit = async () => {
    if (!memberRejectingId) return;
    setMemberActionLoading(true);
    try {
      await adminService.reviewMemberPetition(memberRejectingId, {
        status: MemberPetitionReviewStatus.Rejected,
        rejectionReason: memberRejectionReason.trim() || undefined,
      });
      showSuccess(t('petitions.rejectedSuccess'));
      setMemberRejectModalOpen(false);
      const rejectedId = memberRejectingId;
      setMemberRejectingId(null);
      if (memberStatusFilter === '') {
        setMemberPetitions(prev =>
          prev.map(p =>
            p.id === rejectedId ? { ...p, status: MemberPetitionStatus.Rejected } : p
          )
        );
      } else {
        setMemberPetitions(prev => prev.filter(p => p.id !== rejectedId));
        setMemberTotal(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      showError(err.message || t('petitions.reviewFailed'));
    } finally {
      setMemberActionLoading(false);
    }
  };

  const columns: Column<RegistrationPetition>[] = [
    {
      key: 'applicationReferenceNumber',
      header: t('petitions.reference'),
      render: (p) => <span style={{ fontFamily: 'monospace' }}>{p.applicationReferenceNumber}</span>,
    },
    {
      key: 'type',
      header: t('petitions.type'),
      render: (p) =>
        p.type === 'Institution' ? t('petitions.institution') : t('petitions.individual'),
    },
    {
      key: 'applicantName',
      header: t('petitions.applicantName'),
      render: (p) => p.applicantName || '—',
    },
    {
      key: 'email',
      header: t('petitions.email'),
      render: (p) => p.email || '—',
    },
    {
      key: 'submittedAt',
      header: t('petitions.submittedAt'),
      render: (p) => (p.submittedAt ? formatAthensDateTime(p.submittedAt, i18n.language) : '—'),
    },
    {
      key: 'status',
      header: t('petitions.status'),
      render: (p) => {
        const status = normalizeRegistrationStatus(p.status);
        return <StatusBadge $status={status}>{regStatusLabel(p.status, t)}</StatusBadge>;
      },
    },
    {
      key: 'actions',
      header: t('petitions.actions'),
      actions: (p) => (
        <ActionsCell>
          <Button size="sm" variant="primary" onClick={() => handleView(p)}>
            {t('petitions.review')}
          </Button>
        </ActionsCell>
      ),
    },
  ];

  const memberColumns: Column<MemberPetition>[] = [
    {
      key: 'petitionType',
      header: t('petitions.type'),
      render: (p) => memberPetitionTypeLabel(p.petitionType, t),
    },
    {
      key: 'memberName',
      header: t('petitions.applicantName'),
      render: (p) => p.memberName || '—',
    },
    {
      key: 'email',
      header: t('petitions.email'),
      render: (p) => p.email || '—',
    },
    {
      key: 'status',
      header: t('petitions.status'),
      render: (p) => (
        <StatusBadge $status={p.status}>
          {memberPetitionStatusLabel(p.status, t)}
        </StatusBadge>
      ),
    },
    {
      key: 'currentMembershipType',
      header: t('petitions.currentMembershipType', { defaultValue: 'Current category' }),
      render: (p) =>
        normalizePetitionType(p.petitionType) === PetitionType.ChangeMembershipType
          ? membershipTypeLabel(p.currentMembershipType, t) || '—'
          : '—',
    },
    {
      key: 'targetMembershipType',
      header: t('petitions.targetMembershipType'),
      render: (p) => {
        if (normalizePetitionType(p.petitionType) !== PetitionType.ChangeMembershipType) {
          return formatPetitionPayload(p.payload, t) || '—';
        }
        const parsed = parseMembershipChangePayload(p.payload);
        const label = membershipTypeLabel(p.targetMembershipType ?? parsed.targetMembershipType, t);
        return label || t('petitions.missingTargetCategory', { defaultValue: 'Target category missing' });
      },
    },
    {
      key: 'requestedAt',
      header: t('petitions.requestedAt'),
      render: (p) => (p.requestedAt ? formatAthensDateTime(p.requestedAt, i18n.language) : '—'),
    },
    {
      key: 'attachment',
      header: t('petitions.attachmentOptional'),
      render: (p) =>
        p.attachmentDocumentId ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDownloadAttachment(p.attachmentDocumentId!, p.attachmentFileName)}
          >
            {t('common.download')}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: t('petitions.actions'),
      actions: (p) => (
        <ActionsCell>
          <Button size="sm" variant="outline" onClick={() => setMemberDetailPetition(p)} disabled={memberActionLoading}>
            {t('petitions.viewDetails')}
          </Button>
          {p.status === MemberPetitionStatus.Pending && (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => setMemberApprovePetition(p)}
                disabled={memberActionLoading || !membershipChangeIsApprovable(p)}
                title={
                  membershipChangeIsApprovable(p)
                    ? undefined
                    : t('petitions.cannotApproveMissingTarget', {
                        defaultValue: 'Cannot approve: target membership category is missing.',
                      })
                }
              >
                {t('petitions.approve')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openMemberRejectModal(p.id)} disabled={memberActionLoading}>
                {t('petitions.reject')}
              </Button>
            </>
          )}
        </ActionsCell>
      ),
    },
  ];

  const tabs = [
    { id: 'registration', label: t('petitions.registrationApplications') },
    { id: 'member', label: t('petitions.memberPetitions') },
  ];

  return (
    <PetitionsContainer>
      <PageHeader
        title={t('nav.petitions')}
        subtitle={t('petitions.subtitle')}
      />

      <Tabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        tabs={tabs}
        aria-label={t('petitions.title')}
      >
        <TabPanel tabId="registration">
          <FiltersSection>
            <FiltersGrid>
              <div>
                <FilterLabel htmlFor="admin-petitions-reg-status">{t('petitions.filterStatus')}</FilterLabel>
                <SelectStyled
                  id="admin-petitions-reg-status"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value === '' ? '' : (Number(e.target.value) as RegistrationApplicationStatus))}
                >
                  <option value="">{t('petitions.all')}</option>
                  <option value={1}>{t('petitions.pending')}</option>
                  <option value={2}>{t('petitions.approved')}</option>
                  <option value={3}>{t('petitions.rejected')}</option>
                  <option value={4}>{t('petitions.statusNeedsChanges')}</option>
                </SelectStyled>
              </div>
              <div>
                <FilterLabel htmlFor="admin-petitions-reg-type">{t('petitions.filterType')}</FilterLabel>
                <SelectStyled id="admin-petitions-reg-type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="">{t('petitions.all')}</option>
                  <option value="Individual">{t('petitions.individual')}</option>
                  <option value="Institution">{t('petitions.institution')}</option>
                </SelectStyled>
              </div>
            </FiltersGrid>
          </FiltersSection>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <DataTable<RegistrationPetition>
              columns={columns}
              data={petitions}
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              emptyMessage={t('petitions.empty')}
            />
          )}
        </TabPanel>

        <TabPanel tabId="member">
          <FiltersSection>
            <FiltersGrid>
              <div>
                <FilterLabel htmlFor="admin-petitions-member-type">{t('petitions.filterType')}</FilterLabel>
                <SelectStyled
                  id="admin-petitions-member-type"
                  value={memberTypeFilter}
                  onChange={e => setMemberTypeFilter(e.target.value === '' ? '' : (Number(e.target.value) as PetitionType))}
                >
                  <option value="">{t('petitions.all')}</option>
                  <option value={PetitionType.LostMemberCard}>{t('petitions.types.lostMemberCard')}</option>
                  <option value={PetitionType.ChangeMembershipType}>{t('petitions.types.changeMembershipType')}</option>
                </SelectStyled>
              </div>
              <div>
                <FilterLabel htmlFor="admin-petitions-member-status">{t('petitions.filterStatus')}</FilterLabel>
                <SelectStyled
                  id="admin-petitions-member-status"
                  value={memberStatusFilter}
                  onChange={e => setMemberStatusFilter(e.target.value === '' ? '' : (Number(e.target.value) as MemberPetitionStatus))}
                >
                  <option value="">{t('petitions.all')}</option>
                  <option value={MemberPetitionStatus.Pending}>{t('petitions.statusPending')}</option>
                  <option value={MemberPetitionStatus.Approved}>{t('petitions.statusApproved')}</option>
                  <option value={MemberPetitionStatus.Rejected}>{t('petitions.statusRejected')}</option>
                </SelectStyled>
              </div>
            </FiltersGrid>
          </FiltersSection>

          {memberLoading ? (
            <LoadingSpinner />
          ) : (
            <DataTable<MemberPetition>
              columns={memberColumns}
              data={memberPetitions}
              page={memberPage}
              pageSize={PAGE_SIZE}
              total={memberTotal}
              onPageChange={setMemberPage}
              emptyMessage={t('petitions.empty')}
            />
          )}
        </TabPanel>
      </Tabs>

      <Modal
        isOpen={rejectModalOpen}
        onClose={() => { setRejectModalOpen(false); setRejectingId(null); }}
        title={t('petitions.rejectTitle')}
      >
        <div style={{ marginBottom: spacing[4] }}>
          <Label>{t('petitions.rejectionReason')}</Label>
          <input
            type="text"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder={t('petitions.rejectionReasonPlaceholder')}
            style={{
              width: '100%',
              padding: spacing[3],
              border: `1px solid var(--color-border)`,
              borderRadius: 8,
            }}
          />
        </div>
        <Stack direction="row" spacing="md" justify="end">
          <Button variant="outline" onClick={() => { setRejectModalOpen(false); setRejectingId(null); }}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleRejectSubmit} loading={actionLoading}>
            {t('petitions.rejectConfirm')}
          </Button>
        </Stack>
      </Modal>

      <Modal
        isOpen={!!memberDetailPetition}
        onClose={() => setMemberDetailPetition(null)}
        title={t('petitions.viewDetails')}
      >
        {memberDetailPetition && (
          <Stack direction="column" spacing="md">
            <div>
              <Label>{t('petitions.type')}</Label>
              <Text>{memberPetitionTypeLabel(memberDetailPetition.petitionType, t)}</Text>
            </div>
            <div>
              <Label>{t('petitions.applicantName')}</Label>
              <Text>{memberDetailPetition.memberName || '—'}</Text>
            </div>
            <div>
              <Label>{t('petitions.email')}</Label>
              <Text>{memberDetailPetition.email || '—'}</Text>
            </div>
            <div>
              <Label>{t('petitions.requestedAt')}</Label>
              <Text>
                {memberDetailPetition.requestedAt
                  ? formatAthensDateTime(memberDetailPetition.requestedAt, i18n.language)
                  : '—'}
              </Text>
            </div>
            {normalizePetitionType(memberDetailPetition.petitionType) ===
              PetitionType.ChangeMembershipType && (
              <>
                <div>
                  <Label>{t('petitions.currentMembershipType', { defaultValue: 'Current category' })}</Label>
                  <Text>
                    {membershipTypeLabel(memberDetailPetition.currentMembershipType, t) || '—'}
                  </Text>
                </div>
                <div>
                  <Label>{t('petitions.targetMembershipType')}</Label>
                  <Text>
                    {membershipTypeLabel(
                      memberDetailPetition.targetMembershipType ??
                        parseMembershipChangePayload(memberDetailPetition.payload).targetMembershipType,
                      t
                    ) || t('petitions.missingTargetCategory', { defaultValue: 'Target category missing' })}
                  </Text>
                </div>
              </>
            )}
            <div>
              <Label>{t('petitions.sectionDetails')}</Label>
              <Text>
                {parseMembershipChangePayload(memberDetailPetition.payload).reason ||
                  formatPetitionPayload(memberDetailPetition.payload, t) ||
                  t('petitions.noReasonProvided')}
              </Text>
            </div>
            {memberDetailPetition.attachmentDocumentId && (
              <div>
                <Label>{t('petitions.attachmentOptional')}</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    handleDownloadAttachment(
                      memberDetailPetition.attachmentDocumentId!,
                      memberDetailPetition.attachmentFileName
                    )
                  }
                >
                  {t('common.download')} {memberDetailPetition.attachmentFileName || ''}
                </Button>
              </div>
            )}
            {normalizePetitionType(memberDetailPetition.petitionType) ===
              PetitionType.ChangeMembershipType &&
              !membershipChangeIsApprovable(memberDetailPetition) && (
                <Text color="error" role="alert">
                  {t('petitions.legacyMissingTargetWarning', {
                    defaultValue:
                      'This legacy request has no explicit target category. Do not approve it. Reject it or ask the member to resubmit with a category. The reason text is not used to infer a category.',
                  })}
                </Text>
              )}
            {memberDetailPetition.status === MemberPetitionStatus.Pending && (
              <Stack direction="row" spacing="md" justify="end">
                <Button variant="outline" onClick={() => setMemberDetailPetition(null)}>
                  {t('common.close')}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    openMemberRejectModal(memberDetailPetition.id);
                    setMemberDetailPetition(null);
                  }}
                  disabled={memberActionLoading}
                >
                  {t('petitions.reject')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setMemberApprovePetition(memberDetailPetition);
                  }}
                  disabled={memberActionLoading || !membershipChangeIsApprovable(memberDetailPetition)}
                >
                  {t('petitions.approve')}
                </Button>
              </Stack>
            )}
          </Stack>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!memberApprovePetition}
        onClose={() => {
          if (!memberActionLoading) setMemberApprovePetition(null);
        }}
        onConfirm={() => {
          if (memberApprovePetition && membershipChangeIsApprovable(memberApprovePetition)) {
            void handleMemberApprove(memberApprovePetition);
          }
        }}
        title={t('petitions.approveConfirmTitle', { defaultValue: 'Approve petition?' })}
        message={
          memberApprovePetition
            ? t('petitions.approveConfirmMemberNamed', {
                name: memberApprovePetition.memberName || memberApprovePetition.email || '—',
                type: memberPetitionTypeLabel(memberApprovePetition.petitionType, t),
                details:
                  formatPetitionPayload(memberApprovePetition.payload, t) ||
                  memberPetitionTypeLabel(memberApprovePetition.petitionType, t),
                defaultValue:
                  'Approve {{type}} for {{name}}? {{details}} This applies the reviewed decision once and cannot be undone from this dialog.',
              })
            : ''
        }
        confirmText={t('petitions.approve')}
        cancelText={t('common.cancel')}
        variant="primary"
        loading={memberActionLoading}
        closeOnConfirm={false}
      />

      <Modal
        isOpen={memberRejectModalOpen}
        onClose={() => { setMemberRejectModalOpen(false); setMemberRejectingId(null); }}
        title={t('petitions.rejectTitle')}
      >
        <div style={{ marginBottom: spacing[4] }}>
          <Label>{t('petitions.rejectionReason')}</Label>
          <input
            type="text"
            value={memberRejectionReason}
            onChange={e => setMemberRejectionReason(e.target.value)}
            placeholder={t('petitions.rejectionReasonPlaceholder')}
            style={{
              width: '100%',
              padding: spacing[3],
              border: `1px solid var(--color-border)`,
              borderRadius: 8,
            }}
          />
        </div>
        <Stack direction="row" spacing="md" justify="end">
          <Button variant="outline" onClick={() => { setMemberRejectModalOpen(false); setMemberRejectingId(null); }}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleMemberRejectSubmit} loading={memberActionLoading}>
            {t('petitions.rejectConfirm')}
          </Button>
        </Stack>
      </Modal>
    </PetitionsContainer>
  );
};

export default AdminRegistrationPetitions;
